import { useEffect, useState } from 'react';
import UpdatedAtBubble from './UpdatedAtBubble';
import goldcastLogoUrl from '@/assets/logo/goldcast-logo-primary.svg';
import Button from '@/components/atoms/Button/Button';
import IconButton from '@/components/atoms/Button/IconButton';
import { useAppContext } from '@/context/AppContext/AppContext';
import { showSuccessToast } from '@/libs/toast/toast';
import { Clip } from '@/domains/asset';
import useAnalytics from '@/hooks/useAnalytics';
import { copyTextToClipboard } from '@/Pages/PostsListPage/utils';
import { handleClipLockedError } from '@/Pages/Clip/utils';

enum AuthState {
  SIGNED_IN,
  SIGNED_OUT,
  SIGNED_IN_WITHOUT_ACCESS
}

const SharedClipPageHeader = ({ clip }: { clip: Clip }) => {
  const { logger, adminAppStore } = useAppContext();
  const { trackContentShare } = useAnalytics();

  const [authState, setAuthState] = useState<AuthState>(AuthState.SIGNED_OUT);

  useEffect(() => {
    if (!clip.content?.organization || !adminAppStore.user?.organization) {
      setAuthState(AuthState.SIGNED_OUT);
    } else if (adminAppStore.user?.organization === clip.content?.organization) {
      setAuthState(AuthState.SIGNED_IN);
    } else if (adminAppStore.user?.organization) {
      setAuthState(AuthState.SIGNED_IN_WITHOUT_ACCESS);
    }
  }, [clip.content?.organization, adminAppStore.user?.organization]);

  const handleCopyLink = () => {
    copyTextToClipboard(window.location.href, logger);
    trackContentShare({ contentType: 'CLIP', source: 'SharedPage', shareType: 'CopyURL', asset: clip });
    showSuccessToast('Link copied to clipboard!');
  };

  const handleSignInButtonClick = () => {
    window.open(`${window.location.origin}/content/auth/login?redirect=shared/clip/${clip.id}`, '_blank');
  };

  const handleSignUpButtonClick = () => {
    const params = new URLSearchParams({
      utm_medium: 'product',
      utm_source: 'contentlab',
      utm_campaign: 'contentlab',
      utm_content: 'share'
    });
    window.open(`${window.location.origin}/content/auth/signup?${params.toString()}`, '_blank');
  };

  const handleEditInContentLabButtonClick = () => {
    if (clip.locked) {
      handleClipLockedError(clip);
      return;
    }

    const urlSuffix = !!clip.saved_search
      ? `search/${clip.saved_search.id}/clips?activeClipId=${clip.id}`
      : `${clip.event}/${clip.broadcast}/${clip.asset_metadata.is_full_length ? 'recordings' : `clips/${clip.id}`}`;

    window.open(`${window.location.origin}/content/auth/login?redirect=content/${urlSuffix}`, '_blank');
  };

  const handleOpenContentLabButtonClick = () => {
    window.open(`${window.location.origin}/content`, '_blank');
  };

  return (
    <div className="flex border-b bg-white px-4 py-3">
      <div className="flex min-w-0 grow items-center">
        <a href="https://goldcast.io" target="_blank" rel="noreferrer" className="flex h-10 shrink-0 items-center">
          <img src={goldcastLogoUrl} alt="Goldcast Logo" style={{ width: 96 }} />
        </a>
        {clip.title && (
          <>
            <div className="mx-4 hidden h-9 w-[1px] bg-slate-200 md:flex"></div>
            <div className="hidden min-w-0 flex-1 items-center space-x-4 md:flex">
              <div className="min-w-0 shrink truncate text-sm">{clip.title}</div>
              {clip.updated_at && <UpdatedAtBubble updatedAt={clip.updated_at} />}
            </div>
          </>
        )}
      </div>
      <div className="ml-auto flex shrink-0 items-center gap-2 text-sm">
        <IconButton
          variation="text"
          icon="IconLink"
          trackingId="copy-link-button"
          buttonClassName="hidden md:flex"
          onClick={handleCopyLink}
          title="Copy Link"
          content="Copy Link"
        />
        {authState === AuthState.SIGNED_OUT && (
          <>
            <Button
              trackingId="sign-in-button"
              title="Sign In"
              className="hidden md:flex"
              onClick={handleSignInButtonClick}
            >
              Sign In
            </Button>
            <Button
              variation="filled"
              trackingId="try-content-lab-button"
              title="Try Content Lab for Free"
              onClick={handleSignUpButtonClick}
            >
              Try Content Lab for Free
            </Button>
          </>
        )}
        {authState === AuthState.SIGNED_IN && (
          <Button
            variation="filled"
            trackingId="edit-in-content-lab-button"
            title="Edit in Content Lab"
            onClick={handleEditInContentLabButtonClick}
          >
            Edit in Content Lab
          </Button>
        )}
        {authState === AuthState.SIGNED_IN_WITHOUT_ACCESS && (
          <Button
            variation="filled"
            trackingId="open-content-lab-button"
            title="Open Content Lab"
            onClick={handleOpenContentLabButtonClick}
          >
            Open Content Lab
          </Button>
        )}
      </div>
    </div>
  );
};

export default SharedClipPageHeader;
